import { useCallback, useEffect } from 'react';
import { create } from 'zustand';

// ---------------------------------------------------------------------------
// Message queue — one place that decides WHEN a proactive message (nudge card,
// coach mark, prompt) is allowed on screen, so several of them never land on
// the member at once.
//
// Every message registers as a candidate while it has something to say
// (`ready`) and names its lane:
//
//   inline    — the one card slot in the page flow (reward / reorder / offer
//               cards above the box builder). Shows straight away — pushing
//               page content around to pace it would shift layout — and holds
//               exactly ONE card at a time: the next one (by priority) takes
//               the slot the moment the member dismisses the current one, so
//               it grows in while the old card collapses (NudgeCard animates
//               inline height) and the page never jumps.
//   floating  — anything over the page (coach marks, bottom prompts). At most
//               one at a time, highest priority first; nothing floats in until
//               the page has settled, and there is a quiet gap after any
//               message (either lane) appears or a floating one leaves.
//
// Per-candidate knobs: `delayMs` (earliest showing, from registration) and
// `maxWaitMs` (reactive prompts like "pairs well with…" go stale — drop them
// if the slot isn't free in time; the hook reports this as `expired`).
//
// Both lanes also wait for a pause in member activity (IDLE_MS without a
// scroll / pointer / key / touch): a nudge that lands mid-task is a
// distraction, the same nudge in a natural lull is a suggestion. Activity
// only defers the SHOWING — an already-visible message never hides.
// ---------------------------------------------------------------------------

export const SETTLE_MS = 1200;
export const GAP_MS = 2500;
export const IDLE_MS = 2500;

export const LANES = ['inline', 'floating'];

const now = () => Date.now();

// Last member interaction. 0 = none seen yet (fresh page, member just
// reading), which counts as idle so an untouched page still gets its
// messages on the normal schedule.
let lastActivityAt = 0;

export const markActivity = () => {
  lastActivityAt = now();
};

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
if (typeof window !== 'undefined') {
  // Capture phase so scrolls inside nested containers (drawers, trays) count
  // too; passive so this never delays the interaction itself. No re-evaluate
  // on activity — the scheduled wake re-checks idleness and pushes itself
  // back, so interactions stay free of queue work.
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, markActivity, { capture: true, passive: true })
  );
}

const emptyLanes = () => ({ inline: null, floating: null });

const useMessageQueue = create(() => ({
  candidates: {},
  active: emptyLanes(),
  expired: {},
  firstRegisteredAt: 0,
  lastShownAt: 0,
  lastReleasedAt: 0,
}));

let timer = null;

const scheduleEvaluate = (at) => {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    evaluate();
  }, Math.max(0, at - now()));
};

const byPriority = (a, b) => (b.priority - a.priority) || (a.registeredAt - b.registeredAt);

const minOf = (values, current) => {
  if (values.length === 0) return current;
  const m = Math.min(...values);
  return current === null ? m : Math.min(current, m);
};

function evaluate() {
  const state = useMessageQueue.getState();
  const t = now();
  const candidates = { ...state.candidates };
  const expired = { ...state.expired };
  const active = { ...state.active };
  let { lastShownAt } = state;
  let changed = false;
  let nextWake = null;

  // Reactive prompts that couldn't get their slot in time are dropped.
  Object.values(candidates).forEach((c) => {
    if (c.expiresAt && t >= c.expiresAt && active[c.lane] !== c.id) {
      delete candidates[c.id];
      expired[c.id] = true;
      changed = true;
    }
  });

  // A message may only take a free slot in a lull: IDLE_MS after the last
  // member interaction. Ongoing activity keeps pushing this back; each wake
  // re-reads it, so the next evaluation self-corrects.
  const idleReadyAt = lastActivityAt ? lastActivityAt + IDLE_MS : 0;

  LANES.forEach((lane) => {
    const inLane = Object.values(candidates).filter((c) => c.lane === lane);
    if (active[lane] || inLane.length === 0) return;

    // Floating messages are paced by settle/gap; both lanes wait for the lull.
    const earliest = Math.max(
      idleReadyAt,
      lane === 'floating'
        ? Math.max(
          state.firstRegisteredAt + SETTLE_MS,
          lastShownAt + GAP_MS,
          state.lastReleasedAt + GAP_MS
        )
        : 0
    );

    if (t < earliest) {
      nextWake = minOf([earliest], nextWake);
      return;
    }
    const eligible = inLane.filter((c) => t >= c.notBefore).sort(byPriority);
    if (eligible.length > 0) {
      active[lane] = eligible[0].id;
      lastShownAt = t;
      changed = true;
    } else {
      nextWake = minOf(inLane.map((c) => c.notBefore), nextWake);
    }
  });

  // Wake up to drop stale reactive prompts even while their lane is busy.
  nextWake = minOf(
    Object.values(candidates)
      .filter((c) => c.expiresAt && active[c.lane] !== c.id)
      .map((c) => c.expiresAt),
    nextWake
  );

  if (changed) {
    useMessageQueue.setState({ candidates, expired, active, lastShownAt });
  }
  if (nextWake !== null) scheduleEvaluate(nextWake);
}

export function registerMessage(id, { priority = 0, lane = 'floating', delayMs = 0, maxWaitMs = null } = {}) {
  const state = useMessageQueue.getState();
  const t = now();
  if (state.candidates[id]) {
    updateMessage(id, { priority, lane, delayMs, maxWaitMs });
    return;
  }
  // An empty queue starts a fresh settle window (new page / section).
  const isFirst = Object.keys(state.candidates).length === 0;
  useMessageQueue.setState({
    firstRegisteredAt: isFirst ? t : state.firstRegisteredAt,
    candidates: {
      ...state.candidates,
      [id]: {
        id,
        priority,
        lane,
        registeredAt: t,
        notBefore: t + delayMs,
        expiresAt: maxWaitMs ? t + maxWaitMs : null,
      },
    },
  });
  evaluate();
}

export function updateMessage(id, { priority, lane, delayMs, maxWaitMs } = {}) {
  const state = useMessageQueue.getState();
  const existing = state.candidates[id];
  if (!existing) return;
  const t = now();
  const next = { ...existing };
  if (priority !== undefined) next.priority = priority;
  if (lane !== undefined && state.active[existing.lane] !== id) next.lane = lane;
  // A shorter delay pulls the showing forward; a longer one never pushes a
  // message that was already due back out.
  if (delayMs !== undefined) next.notBefore = Math.min(existing.notBefore, t + delayMs);
  if (maxWaitMs !== undefined) next.expiresAt = maxWaitMs ? existing.registeredAt + maxWaitMs : null;
  useMessageQueue.setState({ candidates: { ...state.candidates, [id]: next } });
  evaluate();
}

export function unregisterMessage(id) {
  const state = useMessageQueue.getState();
  const activeLane = LANES.find((lane) => state.active[lane] === id) || null;
  if (!activeLane && !state.candidates[id] && !state.expired[id]) return;

  const candidates = { ...state.candidates };
  const expired = { ...state.expired };
  delete candidates[id];
  delete expired[id];
  useMessageQueue.setState({
    candidates,
    expired,
    active: activeLane ? { ...state.active, [activeLane]: null } : state.active,
    lastReleasedAt: activeLane === 'floating' ? now() : state.lastReleasedAt,
  });
  evaluate();
}

// Test-only: forget everything, including the cross-page pacing timestamps.
export function resetMessageQueue() {
  if (timer) clearTimeout(timer);
  timer = null;
  lastActivityAt = 0;
  useMessageQueue.setState({
    candidates: {},
    active: emptyLanes(),
    expired: {},
    firstRegisteredAt: 0,
    lastShownAt: 0,
    lastReleasedAt: 0,
  });
}

/**
 * Claim a slot in the message queue while `ready` is true.
 *
 * Returns `active` (render the message now), `expired` (a `maxWaitMs`
 * message that never got its turn — clear it), and `release()` to hand the
 * slot back when the message hides. Turning `ready` off also releases.
 */
export function useMessageSlot({ id, priority = 0, lane = 'floating', ready = true, delayMs = 0, maxWaitMs = null }) {
  const active = useMessageQueue((s) => s.active.inline === id || s.active.floating === id);
  const expired = useMessageQueue((s) => !!s.expired[id]);

  useEffect(() => {
    if (!ready) return undefined;
    registerMessage(id, { priority, lane, delayMs, maxWaitMs });
    return () => unregisterMessage(id);
    // Option changes are applied in place below so they can't drop an active
    // message by re-registering it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ready]);

  useEffect(() => {
    if (ready) updateMessage(id, { priority, lane, delayMs, maxWaitMs });
  }, [id, ready, priority, lane, delayMs, maxWaitMs]);

  const release = useCallback(() => unregisterMessage(id), [id]);

  return { active, expired, release };
}

export default useMessageQueue;
